import { useEffect, useState } from "react";
import { adjustPoints } from "../../api/points";
import { Alert, Button, Dialog, Field, FormGrid, Input } from "../../ui";

type AdjustPointsDialogProps = {
  isOpen: boolean;
  type: "credit" | "debit";
  userId: string;
  playerName: string;
  onClose: (updated?: boolean) => void;
};

const AdjustPointsDialog = (props: AdjustPointsDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    amount: "",
    description: "",
  });

  useEffect(() => {
    if (props.isOpen) {
      setErrorMessage("");
      setFormData({ amount: "", description: "" });
    }
  }, [props.isOpen]);

  const handleClose = (updated?: boolean) => {
    props.onClose(updated);
    setErrorMessage("");
  };

  const handleSubmit = async () => {
    const amount = parseInt(formData.amount, 10);
    if (isNaN(amount) || amount <= 0) {
      setErrorMessage("Please enter a valid whole number of points greater than 0");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      await adjustPoints({
        userId: props.userId,
        amount: props.type === "debit" ? -amount : amount,
        description: formData.description.trim() || undefined,
      });
      handleClose(true);
    } catch (error: any) {
      setErrorMessage(error?.message || `Failed to ${props.type} points`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => handleClose()}
      title={`${props.type === "credit" ? "Credit" : "Debit"} Clash Points`}
      subtitle={
        <>
          {props.type === "credit" ? "Add Clash Points to" : "Deduct Clash Points from"} {props.playerName}'s balance
        </>
      }
      size="sm"
      busy={loading}
      footer={
        <>
          <Button variant="ghost" onClick={() => handleClose()} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant={props.type === "credit" ? "primary" : "danger"}
            onClick={handleSubmit}
            loading={loading}
            disabled={!formData.amount}
          >
            {props.type === "credit" ? "Credit" : "Debit"}
          </Button>
        </>
      }
    >
      <FormGrid columns={1}>
        {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}

        {props.type === "credit" && (
          <Alert tone="info">
            A credit creates a new points lot with a fresh expiry based on the configured validity period.
          </Alert>
        )}

        <Field label="Points" required>
          {(id) => (
            <Input
              id={id}
              type="number"
              min="0"
              step="1"
              placeholder="Enter points"
              value={formData.amount}
              onChange={(e) => setFormData((prev) => ({ ...prev, amount: e.target.value }))}
              disabled={loading}
              autoFocus
            />
          )}
        </Field>

        <Field label="Description (Optional)">
          {(id) => (
            <Input
              id={id}
              type="text"
              placeholder="Reason for adjustment"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              disabled={loading}
            />
          )}
        </Field>
      </FormGrid>
    </Dialog>
  );
};

export default AdjustPointsDialog;
